import { Component, Inject, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { debounceTime, distinctUntilChanged, take } from "rxjs/operators";
import { PacienteService } from "../../services/paciente.service";
import { TurnosService } from "../../services/turnos.service";
import { BehaviorSubject, combineLatest, Subscription } from "rxjs";
import { TipoDocumentoService } from "../../services/tipo-documento.service";
import { CentroMedico } from "../../models/centroMedico.interface";
import { Medico, MedicoConDiasTurno } from "../../models/medico.interface";
import { Agenda } from "../../models/agenda.interface";
import { MatSelectChange } from "@angular/material/select";
import { diasSemanaOrdenAnglo, generarSlotsParaDia, slotsLibres } from "../../utils/turnos.utils";
import { camposFaltantes, mensajeError } from "../../utils/form-errors.utils";
import * as moment from "moment";
import { Paciente, PacienteRequest } from "../../models/paciente.interface";
import { SpinnerService } from "../../services/spinner.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { NotificationComponent } from "../notification/notification.component";
import { ServicioService } from "../../services/servicio.service";

@Component({
  selector: "app-pacient-turno-dialog",
  templateUrl: "./pacient-turno-dialog.component.html",
  styleUrls: ["./pacient-turno-dialog.component.scss"],
})
export class PacientTurnoDialogComponent implements OnInit {
  // Estados del formulario
  isLinear = true;
  paso1Completado = false;
  loadingPacientePorDni = false;
  agregarNuevoPaciente = false;
  pacienteSeleccionado: any = null;

  // Formularios
  formularioPaciente: FormGroup;
  formularioTurno: FormGroup;

  // Datos necesarios
  tiposDocumentos: any[] = [];
  centrosMedicos: any[] = [];
  medicos: any[] = [];
  horarios: string[] = [];

  //datos Iniciales y de formulario
  private subscripciones = new Subscription();
  private diasActivosMédicoObs = new BehaviorSubject<number[]>([
    0, 1, 2, 3, 4, 5, 6, 7,
  ]);
  private diasActivosMedico: number[] = [];
  private listaDeTurnos: any[] = [];

  // Filtros
  filtroMedicos: any[] = [];
  filtroEspecialidades: any[] = [];
  filtroCentroMedicos: any[] = [];

  //Lista de medicos
  listaMedicos: any[] = [];
  listaCompletaMedicos: any[] = [];

  listaEspecialidades: any[] = [];
  listaCentrosMedicos: any[] = [];
  listaCentrosMedicosFiltrados: any[] = [];

  centroMedicoSeleccionado: CentroMedico = null;
  medicoSelecionado: Medico = null;

  // Servicios por especialidad
  listaServicios: any[] = [];
  servicioSeleccionado: any = null;
  mostrarServicioSelect = false;

  //Generacion de turnos
  private _agendaActual: Agenda | null = null;
  private diasSemana = diasSemanaOrdenAnglo;
  private diaActual = new Date();
  fechaError: any;

  general: any;
  requierePago = false;

  constructor(
    private fb: FormBuilder,
    private pacienteService: PacienteService,
    public turnosService: TurnosService,
    private tipoDocumentoService: TipoDocumentoService,
    private dialogRef: MatDialogRef<PacientTurnoDialogComponent>,
    private spinnerService: SpinnerService,
    private snackBar: MatSnackBar,
    private servicioService: ServicioService,
    @Inject(MAT_DIALOG_DATA) public response: any
  ) {}

  ngOnInit() {
    this.general = this.response.general;
    this.requierePago = !!this.response.requierePago;
    this.crearFormularios();
    this.cargarDatosIniciales();
  }

  private cargarDatosIniciales() {
    this.subscripciones.add(
      combineLatest([
        this.turnosService.getDatosAgendar(),
        this.tipoDocumentoService.listar(),
      ])
        .pipe(take(1))
        .subscribe(
          ([
            { especialidades, medicos, centrosMedicos },
            { listaTipoDocumento },
          ]) => {
            this.listaCompletaMedicos = medicos;
            this.filtroMedicos = medicos;
            this.listaEspecialidades = especialidades;
            this.filtroEspecialidades = especialidades;
            this.tiposDocumentos = listaTipoDocumento;

            const centrosConMedicosYEspecialidad = centrosMedicos.filter(
              (centro) =>
                medicos.some(
                  (medico) =>
                    medico.especialidad &&
                    medico.agendaMedico?.some(
                      (agenda) => agenda.id_sucursal === centro.id
                    )
                )
            );

            this.listaCentrosMedicos = centrosConMedicosYEspecialidad;
            this.filtroCentroMedicos = centrosConMedicosYEspecialidad;
          }
        )
    );

    this.subscripciones.add(
      this.diasActivosMédicoObs.subscribe((diasActivosMedico) => {
        this.diasActivosMedico = diasActivosMedico;
      })
    );
  }

  crearFormularios() {
    // Formulario del paso 1 - Buscar o crear paciente
    this.formularioPaciente = this.fb.group({
      dniPaciente: ["", [Validators.required]],
      tipoDniPaciente: [""],
      nombrePaciente: [""],
      apellidoPaciente: [""],
      emailPaciente: ["", [Validators.email]],
    });

    // Formulario del paso 2 - Datos del turno
    const especialidadValidators = this.requierePago
      ? [Validators.required, this.especialidadDistintaDeCeroValidator]
      : [];
    const servicioValidators = this.requierePago ? [Validators.required] : [];

    this.formularioTurno = this.fb.group({
      titulo: [""],
      detalle: [""],
      centroMedico: ["", Validators.required],
      centroMedicoBuscador: [""],
      especialidad: [0, especialidadValidators],
      especialidadBuscador: [""],
      id_medico: ["", Validators.required],
      id_paciente: [""],
      medicoBuscador: [""],
      fechaDesde: ["", Validators.required],
      fechaHasta: [""],
      tiempoDesde: ["", Validators.required],
      tiempoHasta: [""],
      servicio: ["", servicioValidators],
    });
  }

  // Cuando el plan exige pago, la especialidad "0" (Todas) no es válida
  private especialidadDistintaDeCeroValidator(control: any) {
    return control.value && control.value !== 0 ? null : { especialidadRequerida: true };
  }

  escucharCambiosDni() {
    this.formularioPaciente
      .get("dniPaciente")
      .valueChanges.pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((dni) => {
        if (dni?.length < 7 && !this.pacienteSeleccionado) {
          // this.agregarNuevoPaciente = false;
          this.pacienteSeleccionado = null;
          return;
        }

        if (dni?.length >= 7 && !this.agregarNuevoPaciente) {
          this.buscarPacientePorDni(dni);
        }
      });
  }

  buscarPacientePorDni(dni?: string) {
    this.loadingPacientePorDni = true;

    this.pacienteService
      .buscarPorDni(this.formularioPaciente.value.dniPaciente)
      .subscribe({
        next: ({ Haypaciente, paciente }) => {
          this.loadingPacientePorDni = false;

          if (Haypaciente) {
            this.pacienteSeleccionado = paciente;
            // Habilitar el botón para avanzar al siguiente paso
            this.paso1Completado = true;
          } else {
            this.agregarNuevoPaciente = true;
            // Activar validaciones adicionales para nuevo paciente
            this.activarValidacionesNuevoPaciente();
          }
        },
        error: () => {
          this.loadingPacientePorDni = false;
          this.agregarNuevoPaciente = true;
          this.activarValidacionesNuevoPaciente();
        },
      });
  }

  activarValidacionesNuevoPaciente() {
    // Agregar validación para tipo de documento
    this.formularioPaciente
      .get("tipoDniPaciente")
      .setValidators([Validators.required]);
    this.formularioPaciente
      .get("nombrePaciente")
      .setValidators([Validators.required]);
    this.formularioPaciente
      .get("apellidoPaciente")
      .setValidators([Validators.required]);
    this.formularioPaciente
      .get("emailPaciente")
      .setValidators([Validators.required, Validators.email]);

    this.formularioPaciente.patchValue({
      tipoDniPaciente: 6,
    });

    this.formularioPaciente.get("tipoDniPaciente").updateValueAndValidity();
    this.formularioPaciente.get("nombrePaciente").updateValueAndValidity();
    this.formularioPaciente.get("apellidoPaciente").updateValueAndValidity();
    this.formularioPaciente.get("emailPaciente").updateValueAndValidity();
  }
  cancelarNuevoPaciente() {
    this.agregarNuevoPaciente = false;

    // Limpiar campos de nuevo paciente pero mantener DNI
    this.formularioPaciente.patchValue({
      tipoDniPaciente: "",
      nombrePaciente: "",
      apellidoPaciente: "",
      emailPaciente: "",
    });

    // Desactivar validaciones adicionales
    this.formularioPaciente.get("tipoDniPaciente").clearValidators();
    this.formularioPaciente.get("nombrePaciente").clearValidators();
    this.formularioPaciente.get("apellidoPaciente").clearValidators();
    this.formularioPaciente.get("emailPaciente").clearValidators();

    this.formularioPaciente.get("tipoDniPaciente").updateValueAndValidity();
    this.formularioPaciente.get("nombrePaciente").updateValueAndValidity();
    this.formularioPaciente.get("apellidoPaciente").updateValueAndValidity();
    this.formularioPaciente.get("emailPaciente").updateValueAndValidity();
  }

  avanzarAlPaso2() {
    // Verificar si el paso 1 está completo
    if (
      this.pacienteSeleccionado ||
      (this.agregarNuevoPaciente && this.formularioPaciente.valid)
    ) {
      this.paso1Completado = true;
    }
  }

  guardarTurno() {
    if (this.formularioTurno.invalid) {
      this.openSnackBar(
        camposFaltantes(this.formularioTurno, {
          centroMedico: "Centro médico",
          especialidad: "Especialidad",
          servicio: "Servicio",
          id_medico: "Médico",
          fechaDesde: "Fecha",
          tiempoDesde: "Horarios",
        }),
        "error"
      );
      return;
    }
    if (this.agregarNuevoPaciente) {
      const newPaciente: PacienteRequest = {
        nombreApellido:
          this.formularioPaciente.value.nombrePaciente +
          " " +
          this.formularioPaciente.value.apellidoPaciente,
        tipoDocumento: this.formularioPaciente.value.tipoDniPaciente,
        numeroDocumento: this.formularioPaciente.value.dniPaciente,
        email: this.formularioPaciente.value.emailPaciente,
        password: this.formularioPaciente.value.dniPaciente,
        numeroAfiliado: "",
        direccion: "",
        imagen: "",
        obraSocial: "",
        telPais: "",
        telCaract: "",
        telNumero: "",
        celPais: "",
        celCaract: "",
        celNumero: "",
        codigoInterno: "",
        datoingreso: "",
        modificar: true,
      };

      this.spinnerService.mostrarSpinner();
      this.pacienteService
        .crear("", newPaciente, null)
        .pipe(take(1))
        .subscribe({
          next: (data: { mensaje: string; nuevoPaciente?: Paciente }) => {
            this.formularioTurno.patchValue({
              id_paciente: data?.nuevoPaciente?.id,
            });

            this.formatearYEnviarTurno();
          },
          error: (err) => {
            this.spinnerService.ocultarSpinner();
            this.openSnackBar(mensajeError(err, "Error al crear el paciente"), "error");
          },
        });
    } else {
      this.formularioTurno.patchValue({
        id_paciente: this.pacienteSeleccionado.id,
        dniPaciente: this.pacienteSeleccionado.numeroDocumento,
      });

      this.formatearYEnviarTurno();
    }
  }

  private formatearYEnviarTurno() {
    this.formularioTurno.patchValue({
      fechaHasta: this.formularioTurno.value.fechaDesde,
      tiempoHasta: this.formularioTurno.value.tiempoDesde,
    });

    const fechaDesde = this.formatearFecha(
      this.formularioTurno.get("fechaDesde").value,
      this.formularioTurno.get("tiempoDesde").value
    );

    const fechaHasta = this.formatearFecha(
      this.formularioTurno.get("fechaHasta").value,
      this.formularioTurno.get("tiempoHasta").value
    );

    // this.validarFecha(this.formularioTurno.value, fechaDesde, fechaHasta);
    this.crearTurno(this.formularioTurno.value, fechaDesde, fechaHasta);
  }

  private formatearFecha(fechaAFormatear: Date, tiempoAFormatear): string {
    return `${moment(fechaAFormatear).format(
      "YYYY-MM-DD"
    )} ${tiempoAFormatear}`;
  }

  validarFecha(formularioTurno: any, fechaDesde: any, fechaHasta: any) {
    // if (this.pacienteSeleccionado !== null) {
    this.spinnerService.mostrarSpinner();
    this.turnosService
      .validarTurno(formularioTurno, fechaDesde, fechaHasta)
      .subscribe(
        (data) => {
          let response: any = data;
          this.fechaError = response.fechaError;
          if (this.fechaError == "") {
            this.crearTurno(formularioTurno, fechaDesde, fechaHasta);
          } else {
            this.spinnerService.ocultarSpinner();
            this.openSnackBar("Guardado fallido", "error");
          }
        },
        (error) => {
          console.log(error);
          this.spinnerService.ocultarSpinner();
          this.openSnackBar("Guardado fallido", "error");
        }
      );
  }

  crearTurno(formularioTurno: any, fechaDesde: any, fechaHasta: any) {
    if (!this.agregarNuevoPaciente) {
      this.spinnerService.mostrarSpinner();
    }
    this.turnosService
      .crearTurno(formularioTurno, fechaDesde, fechaHasta)
      .subscribe(
        (data) => {
          this.spinnerService.ocultarSpinner();
          this.openSnackBar("Guardado exitoso", "exito");
          this.dialogRef.close({ estado: 1 });
        },
        (error) => {
          this.spinnerService.ocultarSpinner();
          this.openSnackBar(mensajeError(error, "Guardado fallido"), "error");
        }
      );
  }

  deseleccionarPaciente() {
    // Limpiar paciente seleccionado
    this.pacienteSeleccionado = null;

    // Habilitar nuevamente el input de DNI para buscar otro
    this.formularioPaciente.get("dniPaciente").enable();

    // Marcar el paso como incompleto
    this.paso1Completado = false;
  }

  validarCentroMedico(centroMedicoId: number) {
    this.centroMedicoSeleccionado = this.filtroCentroMedicos.find(
      (cm: CentroMedico) => cm.id == centroMedicoId
    );

    // Filtrar médicos que trabajan en este centro médico
    let array = [];
    for (let ops of this.listaCompletaMedicos) {
      if (
        ops?.agendaMedico
          .map((a) => a.id_sucursal)
          .includes(this.centroMedicoSeleccionado?.id)
      ) {
        let nuevoM: any = ops;
        array.push(nuevoM);
      }
    }
    this.listaMedicos = array;
    this.filtroMedicos = array;

    // Ahora filtramos las especialidades basándonos solo en los médicos de este centro médico
    if (array.length > 0) {
      let especialidadesDisponibles = [];
      array.forEach((medico) => {
        if (
          medico.especialidad &&
          !especialidadesDisponibles.some((e) => e.id == medico.especialidad)
        ) {
          const especialidad = this.listaEspecialidades.find(
            (e) => e.id == medico.especialidad
          );
          if (especialidad) {
            especialidadesDisponibles.push(especialidad);
          }
        }
      });
      this.filtroEspecialidades = especialidadesDisponibles;
    } else {
      this.filtroEspecialidades = [];
    }

    // Resetear los campos del formulario
    this.formularioTurno.patchValue({
      id_medico: "",
      fechaDesde: "",
      tiempoDesde: "",
      especialidad: 0,
    });
  }

  filtrarCentroMedico() {
    let valorString: string = this.formularioTurno.get(
      "centroMedicoBuscador"
    ).value;
    this.filtroCentroMedicos = [];

    for (let ops of this.listaCentrosMedicos) {
      if (ops.nombre.toUpperCase().includes(valorString.toUpperCase())) {
        let nuevoCM: any = ops;
        this.filtroCentroMedicos.push(nuevoCM);
      }
    }
  }

  filtrarEspecialidad() {
    let valorString: string = this.formularioTurno.get(
      "especialidadBuscador"
    ).value;
    let array = [];
    for (let ops of this.listaEspecialidades) {
      if (ops.texto.toUpperCase().includes(valorString.toUpperCase())) {
        let nuevoE: any = ops;
        array.push(nuevoE);
      }
    }
    this.filtroEspecialidades = array;
  }

  get duracionTurnoActual(): string {
    return this.servicioSeleccionado?.duracion || this.general?.rangoHasta;
  }

  cargarServicios(especialidadId: number) {
    this.listaServicios = [];
    this.servicioSeleccionado = null;
    this.mostrarServicioSelect = false;
    this.formularioTurno.patchValue({ servicio: '' });

    if (especialidadId && especialidadId !== 0) {
      this.servicioService.listar(especialidadId).subscribe(({ listaServicios }) => {
        this.listaServicios = listaServicios;
        if (listaServicios.length === 1) {
          this.servicioSeleccionado = listaServicios[0];
          this.formularioTurno.patchValue({ servicio: listaServicios[0].id });
          // Si el plan exige pago, mostramos el select igual para que el paciente
          // vea y confirme el servicio que está contratando.
          this.mostrarServicioSelect = this.requierePago;
        } else if (listaServicios.length > 1) {
          this.mostrarServicioSelect = true;
        }
      });
    }
  }

  seleccionarServicio(servicioId: number) {
    this.servicioSeleccionado = this.listaServicios.find(s => s.id === servicioId) || null;
    this.formularioTurno.patchValue({
      fechaDesde: '',
      tiempoDesde: '',
    });
  }

  filtrarEspecialidadMedico() {
    let especialidad: any = this.formularioTurno.get("especialidad").value;

    // Primero, obtener solo los médicos del centro médico seleccionado
    let medicosDelCentro = [];
    if (this.centroMedicoSeleccionado) {
      for (let ops of this.listaCompletaMedicos) {
        if (
          ops?.agendaMedico
            .map((a) => a.id_sucursal)
            .includes(this.centroMedicoSeleccionado?.id)
        ) {
          medicosDelCentro.push(ops);
        }
      }
    } else {
      medicosDelCentro = this.listaCompletaMedicos;
    }

    // Luego, filtrar por especialidad si una está seleccionada
    if (especialidad == 0) {
      this.listaMedicos = medicosDelCentro;
      this.filtroMedicos = medicosDelCentro;
    } else {
      let array = [];
      for (let ops of medicosDelCentro) {
        if (ops.especialidad == especialidad) {
          array.push(ops);
        }
      }
      this.listaMedicos = array;
      this.filtroMedicos = array;
    }

    // Reiniciar médico seleccionado y campos del formulario
    this.medicoSelecionado = null;
    this._agendaActual = null;
    this.formularioTurno.patchValue({
      id_medico: "",
      fechaDesde: "",
      tiempoDesde: "",
    });

    // Cargar servicios de la especialidad seleccionada
    this.cargarServicios(especialidad);
  }

  handleMedicoSeleccionado(medicoId: number) {
    this.medicoSelecionado = this.filtroMedicos.find(
      (m: MedicoConDiasTurno) => m.id == medicoId
    );

    // Resetear la agenda y buscarla de nuevo
    this._agendaActual = undefined;
    this.formularioTurno.patchValue({
      fechaDesde: "",
    });

    if (this.medicoSelecionado) {
      // Esto solo llamará a getAgendaActual() una vez
      const diasActivos = this.generarDiasSemanaActivo();

      this.diasActivosMédicoObs.next(diasActivos);
      this.turnosService
        .turnoDeUnMedico(medicoId)
        .subscribe((turnos) => (this.listaDeTurnos = turnos));
    }
  }
  public actualizarAgendaActual() {
    if (!this.medicoSelecionado || !this.centroMedicoSeleccionado) {
      this._agendaActual = null;
      return;
    }

    this._agendaActual =
      this.medicoSelecionado.agendaMedico?.find(
        (agenda) => agenda.id_sucursal === this.centroMedicoSeleccionado?.id
      ) || null;
  }

  // Getter para usar en el template
  get agendaActual(): Agenda | null {
    return this._agendaActual;
  }

  public getAgendaActual(): Agenda | null {
    // Si no tenemos médico o centro seleccionado, no hay agenda
    if (!this.medicoSelecionado || !this.centroMedicoSeleccionado) {
      this._agendaActual = null;
      return null;
    }

    // Si aún no hemos cacheado la agenda, búscala ahora
    if (this._agendaActual === undefined) {
      this.actualizarAgendaActual();
    }

    return this._agendaActual;
  }

  private generarDiasSemanaActivo(): number[] {
    const diasSemana: number[] = [];

    // Obtener la agenda una sola vez
    const agendaActual = this.getAgendaActual();
    if (!agendaActual) return diasSemana;

    // Usar la variable local en lugar de llamar al método repetidamente
    if (agendaActual.lunes_desde !== null) diasSemana.push(1);
    if (agendaActual.martes_desde !== null) diasSemana.push(2);
    if (agendaActual.miercoles_desde !== null) diasSemana.push(3);
    if (agendaActual.jueves_desde !== null) diasSemana.push(4);
    if (agendaActual.viernes_desde !== null) diasSemana.push(5);
    if (agendaActual.sabado_desde !== null) diasSemana.push(6);
    if (agendaActual.domingo_desde !== null) diasSemana.push(0);

    return diasSemana;
  }

  filtrarMedico() {
    let valorString: string = this.formularioTurno.get("medicoBuscador").value;
    let array = [];

    for (let ops of this.listaMedicos) {
      if (
        ops.nombreCompleto.toUpperCase().includes(valorString.toUpperCase())
      ) {
        let nuevoM: any = ops;
        array.push(nuevoM);
      }
    }
    this.filtroMedicos = array;
  }

  private generarListaDeTodosLosTurnos(day: Date): string[] {
    const agenda = this.medicoSelecionado ? this.getAgendaActual() : null;
    return generarSlotsParaDia(day, agenda, this.diaActual, this.duracionTurnoActual);
  }

  private turnosPorMedico() {
    const medico = this.formularioTurno.value.id_medico;
    if (!medico) return [];

    return this.listaDeTurnos.filter((turno) => turno.id_medico === medico);
  }

  private horariosLibres(day: Date): string[] {
    return slotsLibres(
      this.generarListaDeTodosLosTurnos(day),
      this.turnosPorMedico(),
      day
    );
  }

  private diaCompleto(day: Date): boolean {
    return this.horariosLibres(day).length === 0;
  }

  private filtrarFechasDatePicker(d: Date | null): boolean {
    const day = d || new Date();

    {
      return (
        this.diasActivosMedico?.includes(day.getDay()) && !this.diaCompleto(day)
      );
    }
  }

  public boundedFiltrarFechasDatePicker =
    this.filtrarFechasDatePicker.bind(this);

  public seleccionarHorario(event: MatSelectChange) {
    this.formularioTurno.patchValue({
      tiempoDesde: event.value.split("-")[0].trim(),
    });
  }

  public listaDeTurnosSinLosYaOtorgados(): string[] {
    const diaSeleccionado = new Date(this.formularioTurno.value.fechaDesde);

    const listaDeTurnosSinLosTurnosOtorgados = this.horariosLibres(diaSeleccionado);

    return listaDeTurnosSinLosTurnosOtorgados.length === 0
      ? ["No quedan turnos disponibles"]
      : listaDeTurnosSinLosTurnosOtorgados;
  }

  cambiarTurno(event: Event, tipoValor: any) {
    this.fechaError = "";
    if (tipoValor == "tiempoDesde") {
      let rangoHasta = moment(this.duracionTurnoActual, "HH:mm");
      let fechaDesde = this.formularioTurno.get("fechaDesde");
      let fechaHasta = this.formularioTurno.get("fechaHasta");
      let tiempoDesde = this.formularioTurno.get("tiempoDesde");
      let tiempoHasta = this.formularioTurno.get("tiempoHasta");
      if (!tiempoHasta.value && rangoHasta.isValid()) {
        let fechaMomentDesde = moment(fechaDesde.value); //.format('YYYY-MM-DD');
        let fechaMomentHasta = moment(fechaHasta.value); //.format('YYYY-MM-DD');
        if (fechaMomentDesde.isSame(fechaMomentHasta)) {
          let fechaTotalDesde = moment(
            `${fechaMomentDesde.format("YYYY-MM-DD")} ${tiempoDesde.value}`,
            "YYYY-MM-DD HH:mm"
          );
          let sumaMoment = fechaTotalDesde
            .add(rangoHasta.hours(), "hours")
            .add(rangoHasta.minutes(), "minutes");
          tiempoHasta.setValue(sumaMoment.format("HH:mm"));
          fechaHasta.setValue(
            new Date(sumaMoment.year(), sumaMoment.month(), sumaMoment.date())
          );
        } else {
          let tiempoMomentDesde = moment(tiempoDesde.value, "HH:mm");
          let sumaMoment = tiempoMomentDesde
            .add(rangoHasta.hours(), "hours")
            .add(rangoHasta.minutes(), "minutes");
          tiempoHasta.setValue(sumaMoment.format("HH:mm"));
        }
      }
    }
  }

  openSnackBar(message: string, action: string) {
    this.snackBar.openFromComponent(NotificationComponent, {
      data: {
        message: message[0].toUpperCase() + message.slice(1),
        action: action,
      },
      duration: 2500,
      horizontalPosition: "right",
      verticalPosition: "top",
      panelClass: [action],
    });
  }
}
